import { useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import AcademicHighlights from "./components/AcademicHighlights";
import CodeSnippetShowcase from "./components/CodeSnippetShowcase";
import CollaborationCTA from "./components/CollaborationCTA";
import FeaturedProjects from "./components/FeaturedProjects";
import Footer from "./components/Footer";
import HeroSection from "./components/HeroSection";
import SkillsShowcase from "./components/SkillsShowcase";
import SocialProof from "./components/SocialProof";
import StatsSection from "./components/StatsSection";
import {
  AcademicHighlight,
  CodeSnippet,
  FeaturedProject,
  HeroSection as HeroSectionType,
  Skill,
  SocialLink,
  Stat,
  Testimonial
} from "./types";

const Homepage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroData: HeroSectionType = {
    title: "Muhammad Fotih Abdullaev",
    subtitle: "Computer Information Technology student at Valencia College combining academic excellence with entrepreneurial ambition. Ready to transform innovative ideas into reality through collaborative partnerships.",
    typingTexts: [
      "React Developer",
      "Python Programmer",
      "Full Stack Engineer",
      "Problem Solver",
      "Tech Enthusiast"],

    ctaButtons: [
      {
        id: "cta-1",
        label: "Let's Collaborate",
        variant: "default",
        icon: "MessageCircle",
        action: "whatsapp"
      },
      {
        id: "cta-2",
        label: "View Projects",
        variant: "outline",
        icon: "FolderGit2",
        action: "navigate",
        href: "/projects"
      },
      {
        id: "cta-3",
        label: "Connect",
        variant: "secondary",
        icon: "Users",
        action: "navigate",
        href: "/contact"
      }]

  };

  const stats: Stat[] = [
    {
      id: "stat-1",
      label: "Projects Completed",
      value: "8",
      icon: "FolderGit2",
      description: "Academic & Personal"
    },
    {
      id: "stat-2",
      label: "Technologies",
      value: "12+",
      icon: "Code2",
      description: "Languages & Frameworks"
    },
    {
      id: "stat-3",
      label: "GPA",
      value: "3.5",
      icon: "Award",
      description: "Valencia College"
    },
    {
      id: "stat-4",
      label: "GitHub Repos",
      value: "10",
      icon: "Github",
      description: "Open Source"
    }];


  const featuredProjects: FeaturedProject[] = [
    {
      id: "project-1",
      title: "E-Commerce Platform",
      description: "Full-stack e-commerce solution with React frontend, Node.js backend, and MongoDB database. Features include user authentication, product management, shopping cart, and payment integration.",
      image: "https://images.unsplash.com/photo-1635405074683-96d6921a2a68",
      alt: "Modern e-commerce website interface showing product grid with shopping cart and checkout features on laptop screen",
      technologies: ["React", "Node.js", "MongoDB", "Express", "Stripe"],
      liveUrl: "https://example.com",
      githubUrl: "https://github.com/Muhammadninja?tab=repositories",
      category: "Full Stack"
    },
    {
      id: "project-2",
      title: "Task Management App",
      description: "Collaborative task management application with real-time updates, team collaboration features, and project tracking capabilities. Built with modern web technologies.",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_18a15d338-1764530680221.png",
      alt: "Task management dashboard displaying kanban board with color-coded cards, progress bars, and team collaboration interface",
      technologies: ["React", "Firebase", "Tailwind CSS", "TypeScript"],
      liveUrl: "https://example.com",
      githubUrl: "https://github.com/Muhammadninja?tab=repositories",
      category: "Web App"
    },
    {
      id: "project-3",
      title: "Weather Forecast Dashboard",
      description: "Interactive weather dashboard providing real-time weather data, 7-day forecasts, and location-based weather alerts. Features responsive design and data visualization.",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_15889b002-1764530676243.png",
      alt: "Weather dashboard showing temperature graphs, precipitation charts, and forecast cards with colorful weather icons",
      technologies: ["React", "OpenWeather API", "Chart.js", "CSS3"],
      liveUrl: "https://example.com",
      githubUrl: "https://github.com/Muhammadninja?tab=repositories",
      category: "API Integration"
    }];


  const skills: Skill[] = [
    {
      id: "skill-1",
      name: "React",
      icon: "Code2",
      proficiency: 85,
      category: "Frontend Development"
    },
    {
      id: "skill-2",
      name: "TypeScript",
      icon: "FileCode",
      proficiency: 80,
      category: "Frontend Development"
    },
    {
      id: "skill-3",
      name: "Tailwind CSS",
      icon: "Palette",
      proficiency: 90,
      category: "Frontend Development"
    },
    {
      id: "skill-4",
      name: "Python",
      icon: "Terminal",
      proficiency: 85,
      category: "Backend Development"
    },
    {
      id: "skill-5",
      name: "Node.js",
      icon: "Server",
      proficiency: 75,
      category: "Backend Development"
    },
    {
      id: "skill-6",
      name: "MongoDB",
      icon: "Database",
      proficiency: 70,
      category: "Backend Development"
    },
    {
      id: "skill-7",
      name: "Git",
      icon: "GitBranch",
      proficiency: 85,
      category: "Tools & Workflow"
    },
    {
      id: "skill-8",
      name: "VS Code",
      icon: "Code",
      proficiency: 90,
      category: "Tools & Workflow"
    },
    {
      id: "skill-9",
      name: "Figma",
      icon: "Figma",
      proficiency: 75,
      category: "Tools & Workflow"
    }];


  const academicHighlights: AcademicHighlight[] = [
    {
      id: "academic-1",
      title: "Computer Information Technology Major",
      institution: "Valencia College",
      description: "Pursuing Associate of Science degree with focus on software development, data structures, algorithms, and web technologies. Maintaining 3.5 GPA while actively participating in coding clubs and hackathons.",
      icon: "GraduationCap",
      year: "2022-2024"
    },
    {
      id: "academic-2",
      title: "Dean's List Recognition",
      institution: "Valencia College",
      description: "Achieved Dean's List honors for three consecutive semesters, demonstrating consistent academic excellence and dedication to computer information technology studies.",
      icon: "Award",
      year: "2023"
    },
    {
      id: "academic-3",
      title: "Successful Windows Server built success",
      institution: "Valencia College Tech Fest",
      description: "First place winner in annual coding competition, developing innovative solution for campus resource management system within 24-hour hackathon timeframe.",
      icon: "Trophy",
      year: "2023"
    }];


  const codeSnippets: CodeSnippet[] = [
    {
      id: "snippet-1",
      language: "React",
      title: "Custom Hook Example",
      code: `import { useState, useEffect } from 'react'
;\n\nfunction useLocalStorage(key, initialValue) {\n  const [value, setValue] = useState(() => {\n    const item = window.localStorage.getItem(key);\n    return item ? JSON.parse(item) : initialValue;\n  });\n\n  useEffect(() => {\n    window.localStorage.setItem(key, JSON.stringify(value));\n  }, [key, value]);\n\n  return [value, setValue];\n}`
    },
    {
      id: "snippet-2",
      language: "Python",
      title: "Data Processing",
      code: `def process_data(data_list):\n    """Process and transform data efficiently"""\n    processed = []\n    for item in data_list:\n        if item['status'] == 'active':\n            processed.append({\n                'id': item['id'],\n                'value': item['value'] * 2,\n                'timestamp': datetime.now()\n            })\n    return processed`
    },
    {
      id: "snippet-3",
      language: "TypeScript",
      title: "Type-Safe API Call",
      code: `interface ApiResponse<T> {\n  data: T;\n  status: number;\n  message: string;\n}\n\nasync function fetchData<T>(url: string): Promise<ApiResponse<T>> {\n  const response = await fetch(url);\n  const data = await response.json();\n  return {\n    data,\n    status: response.status,\n    message: response.statusText\n  };\n}`
    }];


  const testimonials: Testimonial[] = [
    {
      id: "testimonial-1",
      name: "Dr. Sarah Johnson",
      role: "Computer Information Technology Professor",
      company: "Valencia College",
      content: "Muhammad consistently demonstrates exceptional problem-solving skills and a genuine passion for learning. His ability to grasp complex concepts quickly and apply them creatively sets him apart.",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bcf9ec9b-1763295549607.png",
      alt: "Professional headshot of Dr. Sarah Johnson, computer science professor with glasses and friendly smile in academic office setting"
    },
    {
      id: "testimonial-2",
      name: "Alex Chen",
      role: "Study Group Leader",
      company: "Valencia College",
      content: "Working with Muhammad on group projects has been fantastic. He brings fresh ideas, writes clean code, and is always willing to help teammates understand difficult concepts.",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16e9d8251-1763294843980.png",
      alt: "Casual portrait of Alex Chen, young male student with short dark hair wearing casual shirt in campus environment"
    },
    {
      id: "testimonial-3",
      name: "Maria Rodriguez",
      role: "Hackathon Team Member",
      company: "Tech Fest 2023",
      content: "Muhammad's technical skills and collaborative spirit made our hackathon experience incredible. His ability to quickly prototype solutions and debug complex issues was invaluable to our team's success.",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b3335386-1763298777514.png",
      alt: "Friendly portrait of Maria Rodriguez, young female developer with long brown hair smiling at camera in modern tech workspace"
    }];


  const socialLinks: SocialLink[] = [
    {
      id: "social-1",
      platform: "GitHub",
      icon: "Github",
      url: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja?tab=repositories",
      label: "View GitHub Profile"
    },
    {
      id: "social-2",
      platform: "LinkedIn",
      icon: "Linkedin",
      url: "https://www.linkedin.com/in/fotih-abdullaev-901817356",
      label: "Connect on LinkedIn"
    },
    {
      id: "social-3",
      platform: "Twitter",
      icon: "Twitter",
      url: "https://x.com/muhammad640961?s=21",
      label: "Follow on Twitter"
    },
    {
      id: "social-4",
      platform: "Email",
      icon: "Mail",
      url: "mailto:muhammad@fotih.world",
      label: "Send Email"
    }];


  return (
    <>
      <Helmet>
        <title>Muhammad Fotih Abdullaev - Computer Information Technology Student & Developer</title>
        <meta
          name="description"
          content="Computer Information Technology student at Valencia College passionate about building innovative solutions. Open for collaboration on exciting projects and business partnerships." />

        <meta
          name="keywords"
          content="Muhammad Fotih Abdullaev, Computer Information Technology, Valencia College, Web Developer, React Developer, Full Stack Developer, Orlando Florida" />

        <meta property="og:title" content="Muhammad Fotih Abdullaev - Developer Portfolio" />
        <meta
          property="og:description"
          content="Explore my projects, skills, and academic journey. Let's collaborate on innovative solutions." />

        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <main>
          <HeroSection data={heroData} />
          <StatsSection stats={stats} />
          <FeaturedProjects projects={featuredProjects} />
          <SkillsShowcase skills={skills} />
          <AcademicHighlights highlights={academicHighlights} />
          <CodeSnippetShowcase snippets={codeSnippets} />
          <CollaborationCTA />
          <SocialProof testimonials={testimonials} socialLinks={socialLinks} />
        </main>
        <Footer />
      </div>
    </>);

};

export default Homepage;